/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FAQ_JSONLD_ID, buildFAQPageJsonLd, globalFaqEntries } from '../lib/faqSchema.js';
import { removeJsonLd, upsertJsonLd } from '../lib/jsonLdDom';

export interface FAQEntry {
  question: string;
  answer: string;
}

/**
 * Emits FAQPage JSON-LD on the current page. By default it builds the Q&A from
 * the global `faq.q*` / `faq.a*` translation pairs (the homepage accordion).
 * Pass an explicit `entriesOverride` to emit a page-specific FAQ (e.g. the
 * Zakelijk page) instead — memoise it in the caller so the effect doesn't
 * re-run every render. Use only on pages where the FAQ Q&A is actually
 * visible to the user — Google penalises FAQPage schema whose questions are
 * hidden.
 *
 * The payload builder is shared with the server bake (src/lib/faqSchema.js):
 * the prerender writes the same `#jsonld-faqpage` script into the homepage
 * head for non-JS crawlers, and this hook takes it over in place after
 * hydration — same id, same bytes, never a duplicate.
 */
export function useFAQSchema(entriesOverride?: FAQEntry[]) {
  const { t, language } = useLanguage();

  useEffect(() => {
    const entries: FAQEntry[] =
      entriesOverride && entriesOverride.length > 0
        ? entriesOverride
        : globalFaqEntries(t('faq', { returnObjects: true }));

    const payload = buildFAQPageJsonLd(entries, language);
    upsertJsonLd(FAQ_JSONLD_ID, payload);
    if (!payload) return;

    return () => {
      removeJsonLd(FAQ_JSONLD_ID);
    };
  }, [t, language, entriesOverride]);
}
